import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getNotifications,
  updateNotification,
  updateAllNotification,
  deleteNotification,
  deleteAllNotification,
} from "../../../services/notifications";
import toast from "react-hot-toast";

function useNotifications() {
  const queryClient = useQueryClient();

  // Get user notifications
  const {
    data: notifications,
    isLoading: isLoadingNotifications,
    error: notificationsError,
    refetch: refetchNotifications,
  } = useQuery({
    queryKey: ["notifications"],
    queryFn: getNotifications,
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Mark notification as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: updateNotification,
    onSuccess: () => {
      queryClient.invalidateQueries(["notifications"]);
      toast.success("Notification marked as read");
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.error || "Failed to mark notification as read";
      toast.error(errorMessage);
    },
  });

  // Mark all notifications as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: updateAllNotification,
    onSuccess: () => {
      queryClient.invalidateQueries(["notifications"]);
      toast.success("All notifications marked as read");
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.error ||
        "Failed to mark all notifications as read";
      toast.error(errorMessage);
    },
  });

  // Delete notification mutation
  const deleteNotificationMutation = useMutation({
    mutationFn: deleteNotification,
    onSuccess: () => {
      queryClient.invalidateQueries(["notifications"]);
      toast.success("Notification deleted");
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.error || "Failed to delete notification";
      toast.error(errorMessage);
    },
  });

  // Delete all notifications mutation
  const deleteAllNotificationsMutation = useMutation({
    mutationFn: deleteAllNotification,
    onSuccess: () => {
      queryClient.invalidateQueries(["notifications"]);
      toast.success("All notifications deleted");
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.error || "Failed to delete all notifications";
      toast.error(errorMessage);
    },
  });

  // Helper functions
  const markAsRead = (notificationId) => {
    markAsReadMutation.mutate(notificationId);
  };

  const markAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  const deleteNotificationById = (notificationId) => {
    deleteNotificationMutation.mutate(notificationId);
  };

  const deleteAllNotifications = () => {
    deleteAllNotificationsMutation.mutate();
  };

  // Computed values
  const unreadCount =
    notifications?.notifications?.filter((notification) => !notification.read)
      .length || 0;

  const hasNotifications = notifications?.notifications?.length > 0;

  // Loading states
  const isLoading =
    isLoadingNotifications ||
    markAsReadMutation.isLoading ||
    markAllAsReadMutation.isLoading ||
    deleteNotificationMutation.isLoading ||
    deleteAllNotificationsMutation.isLoading;

  return {
    // Data
    notifications: notifications?.notifications || [],
    pagination: notifications?.pagination,
    unreadCount,
    hasNotifications,

    // Loading states
    isLoading,
    isLoadingNotifications,
    isMarkingAsRead: markAsReadMutation.isLoading,
    isMarkingAllAsRead: markAllAsReadMutation.isLoading,
    isDeleting: deleteNotificationMutation.isLoading,
    isDeletingAll: deleteAllNotificationsMutation.isLoading,

    // Error states
    error: notificationsError,

    // Actions
    markAsRead,
    markAllAsRead,
    deleteNotification: deleteNotificationById,
    deleteAllNotifications,
    refetch: refetchNotifications,
  };
}

export default useNotifications;
